import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchFile } from '@ffmpeg/util';
import { useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { DEFAULT_THUMBNAIL_EXTENSION } from '@/constants';
import { AssetScope, AssetType } from '@/enums/AssetEnums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import { getAssetBlobClient, getVideoThumbnail } from '@/helpers';
import { getFFmpeg } from '@/helpers/ffmpeg';
import { showToast, ToastType } from '@/helpers/toast';
import { DraggableElement } from '@/pages/Event/components/DraggableElement';
import { PauseIcon, PlayIcon } from '@/resources';
import { useCreateAssetMutation, useCreateSasTokenMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { Asset } from '@/types';

const MIN_DURATION = 1;
const START_TIME = '0:00';
const TOLERANCE = 0.1;

type VideoAssetEditProps = {
  asset: Asset;
  eventId?: number;
  closeModal: () => void;
};

export const VideoAssetEdit = ({ asset, eventId, closeModal }: VideoAssetEditProps) => {
  const [t] = useTranslation('common');
  const queryClient = useQueryClient();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastDragEndTime = useRef<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [start, setStart] = useState<number>(0);
  const [end, setEnd] = useState<number>(0);
  const [initialStart, setInitialStart] = useState<number>(0);
  const [initialEnd, setInitialEnd] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);

  const { mutateAsync: createSasToken } = useCreateSasTokenMutation(queryClient, {
    onSuccess: () => undefined,
    onError: () => {
      showToast(ToastType.ERROR, t('sasTokenError'));
    }
  });

  const { mutateAsync: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: () => undefined,
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const handleSaveTrim = async () => {
    if (!asset?.assetUrl || end <= start) {
      showToast(ToastType.ERROR, t('invalidVideoOrTimeRange'));
      return;
    }

    const trimDuration = end - start;
    if (trimDuration < MIN_DURATION) {
      showToast(ToastType.ERROR, t('minimumDurationRequired', { duration: MIN_DURATION }));
      return;
    }

    setProcessing(true);
    try {
      const ffmpeg = await getFFmpeg();

      const inputName = 'input.mp4';
      const outputName = 'trimmed_video.mp4';

      const data = await fetchFile(asset?.assetUrl);

      await ffmpeg.writeFile(inputName, data);

      const ffmpegArgs = [
        '-y',
        '-i',
        inputName,
        '-ss',
        String(start),
        '-t',
        String(trimDuration),
        '-c:v',
        'libx264',
        '-c:a',
        'aac',
        '-preset',
        'ultrafast',
        '-avoid_negative_ts',
        'make_zero',
        outputName
      ];

      await ffmpeg.exec(ffmpegArgs);

      const outputData = (await ffmpeg.readFile(outputName)) as Uint8Array;

      const videoBlob = new Blob([new Uint8Array(outputData)], { type: 'video/mp4' });

      const originalName = (asset?.assetName || 'video').replace(/\.mp4$/i, '');
      const trimmedFilename = `${originalName}_trimmed_${formatTime(start)}-${formatTime(end)}.mp4`;
      const trimmedAssetName = `${originalName}_trimmed_${formatTime(start)}-${formatTime(end)}`;

      await uploadTrimmedVideo(videoBlob, trimmedFilename, trimmedAssetName);

      showToast(ToastType.SUCCESS, t('videoTrimSuccess'));
      closeModal();
    } catch (error) {
      showToast(
        ToastType.ERROR,
        t('videoTrimFailed', { message: error instanceof Error ? error.message : 'Unknown error' })
      );
    } finally {
      setProcessing(false);
    }
  };

  const uploadTrimmedVideo = async (blob: Blob, filename: string, assetName: string) => {
    if (!eventId) {
      showToast(ToastType.ERROR, t('eventIdRequired'));
      return;
    }

    try {
      const sasTokenPayload = {
        id: eventId,
        assetName: '',
        extension: '.mp4',
        isPublic: false,
        isThumbnail: false,
        blobLocation: BlobLocation.EVENTS
      };

      const sasTokenResponse = await createSasToken(sasTokenPayload);
      if (!sasTokenResponse?.data) {
        throw new Error('Failed to get SAS token');
      }

      const sasData = sasTokenResponse.data;

      const assetBlobClient = getAssetBlobClient(sasTokenResponse);

      if (!assetBlobClient) {
        return false;
      }

      const encodedAssetName = encodeURIComponent(assetName + '.mp4')
        .replace(/['()]/g, escape)
        .replace(/\*/g, '%2A')
        .replace(/%20/g, ' ');

      const contentDisposition = `attachment; filename="${encodedAssetName}"`;
      try {
        await assetBlobClient.uploadBrowserData(blob, {
          blobHTTPHeaders: {
            blobContentDisposition: contentDisposition
          }
        });
      } catch (err) {
        throw new Error(
          'Azure upload failed: ' + (err instanceof Error ? err.message : 'Unknown error')
        );
      }

      let thumbnailUrl = '';
      try {
        const thumbnailBlob = (await getVideoThumbnail(
          new File([blob], filename, { type: 'video/mp4' })
        )) as Blob;

        if (thumbnailBlob) {
          const thumbnailSasPayload = {
            id: eventId,
            assetName: sasData.assetName,
            extension: DEFAULT_THUMBNAIL_EXTENSION,
            isPublic: false,
            isThumbnail: true,
            blobLocation: BlobLocation.EVENTS
          };

          const thumbnailSasResponse = await createSasToken(thumbnailSasPayload);
          if (thumbnailSasResponse?.data) {
            const thumbnailBlobClient = getAssetBlobClient(thumbnailSasResponse);
            if (thumbnailBlobClient) {
              await thumbnailBlobClient.uploadBrowserData(thumbnailBlob, {
                blobHTTPHeaders: {
                  blobContentType: 'image/jpeg'
                }
              });
              thumbnailUrl = `${thumbnailSasResponse.data.baseUrl}/${thumbnailSasResponse.data.path}`;
            }
          }
        }
      } catch (thumbnailError) {
        thumbnailUrl = asset?.thumbnailUrl || '';
      }

      const assetPayload = {
        eventId: eventId,
        assetType: AssetType.VIDEO,
        assetScope: AssetScope.PRIVATE,
        assetName: assetName + '.mp4',
        assetUrl: `${sasData.baseUrl}/${sasData.path}`,
        thumbnailUrl: thumbnailUrl,
        assetSize: blob.size,
        additionalData: JSON.stringify({
          originalVideo: asset?.assetUrl,
          startTime: start,
          endTime: end,
          trimmedDuration: Math.round(end - start)
        }),
        duration: Math.round(end - start),
        tags: []
      };

      const assetResponse = await createAsset(assetPayload);
      if (!assetResponse?.data) {
        throw new Error('Failed to create asset');
      }

      showToast(ToastType.SUCCESS, t('uploadSuccess', { id: assetResponse.data.id }));
    } catch (error) {
      if (error instanceof Error) {
        showToast(ToastType.ERROR, t('uploadFailed', { message: error.message }));
      }
    }
  };

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const formatTime = (songTime: number) => {
    const minutes = Math.floor(songTime / 60);
    const seconds = Math.floor(songTime % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleStartChange = (val: number) => {
    const next = clamp(val, 0, end - MIN_DURATION);
    setStart(next);

    if (videoRef.current) {
      const currentPos = videoRef.current.currentTime;
      if (currentPos < next || currentPos > end) {
        videoRef.current.currentTime = next;
        setCurrentTime(next);
      }
    }

    const hasChanged = next !== initialStart || end !== initialEnd;
    setHasChanges(hasChanged);
  };

  const handleEndChange = (val: number) => {
    const next = clamp(val, start + MIN_DURATION, duration);
    setEnd(next);

    if (videoRef.current) {
      const currentPos = videoRef.current.currentTime;
      if (currentPos < start || currentPos > next) {
        videoRef.current.currentTime = start;
        setCurrentTime(start);
      }
    }

    const hasChanged = start !== initialStart || next !== initialEnd;
    setHasChanges(hasChanged);
  };

  const handlePlayPause = () => {
    if (!videoRef.current) {
      return;
    }

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      const currentPos = videoRef.current.currentTime;

      if (currentPos < start - TOLERANCE || currentPos > end + TOLERANCE) {
        videoRef.current.currentTime = start;
        setCurrentTime(start);
      }

      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) {
      return;
    }

    const current = videoRef.current.currentTime;
    setCurrentTime(current);

    if (current >= end) {
      videoRef.current.pause();
      setIsPlaying(false);
      videoRef.current.currentTime = start;
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = start;
    }
  };

  const createProgressDragHandler = () => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = e.currentTarget.parentElement;
      if (!container || !videoRef.current) {
        return;
      }

      let dragging = true;

      const onMove = (moveEvent: MouseEvent) => {
        if (!dragging || !videoRef.current) {
          return;
        }

        const rect = container.getBoundingClientRect();
        const x = moveEvent.clientX - rect.left;
        const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (pct / 100) * duration;

        const constrainedTime = Math.max(start, Math.min(end, time));

        videoRef.current.currentTime = constrainedTime;
        setCurrentTime(constrainedTime);
      };

      const onUp = () => {
        dragging = false;
        lastDragEndTime.current = Date.now();
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };
  };

  const createDragHandler = (changeHandler: (time: number) => void, type: 'start' | 'end') => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = e.currentTarget.parentElement;
      if (!container) {
        return;
      }

      const wasPlaying = isPlaying;
      if (videoRef.current && isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      }

      setIsDragging(type);
      let localDragging = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!localDragging) {
          return;
        }

        const rect = container.getBoundingClientRect();
        const x = moveEvent.clientX - rect.left;
        const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (percent / 100) * duration;
        changeHandler(time);
      };

      const handleMouseUp = () => {
        localDragging = false;
        setIsDragging(null);
        lastDragEndTime.current = Date.now();

        if (videoRef.current && wasPlaying) {
          videoRef.current.play();
          setIsPlaying(true);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !duration) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const time = percent * duration;

    if (isDragging === 'start') {
      handleStartChange(time);
    } else if (isDragging === 'end') {
      handleEndChange(time);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  const handleTimelineClick = (e: React.MouseEvent) => {
    if (isDragging) {
      return;
    }

    const now = Date.now();
    if (now - lastDragEndTime.current < 100) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const time = percent * duration;

    if (videoRef.current) {
      if (time >= start && time <= end) {
        videoRef.current.currentTime = time;
        setCurrentTime(time);
      }
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => closeModal()}
      modalClasses='!h-[620px]'
      containerClasses='max-h-[80vh] !overflow-y-auto'
      label={t('editVideo')}
      height={'h-full'}>
      <div className='w-full border-t' />
      <div
        className='mt-4 w-[600px] max-w-[95vw] space-y-6 px-6'
        data-testid={TestIds.uploadContent.videoTrimModal}>
        <div className='mr-2 flex flex-row items-center justify-center text-sm font-bold'>
          <Label type={LABEL_TYPE.PARAGRAPH2_BOLD} text={asset?.assetName} />
        </div>
        <div className='rounded-xl bg-gray-900'>
          <div className='flex justify-center'>
            <div className='relative w-full max-w-2xl'>
              <div className='group relative aspect-video w-full'>
                <video
                  ref={videoRef}
                  src={asset?.assetUrl}
                  className='size-full rounded-lg object-contain shadow-2xl'
                  controls={false}
                  onLoadedMetadata={(e) => {
                    const videoElement = e.currentTarget as HTMLVideoElement;
                    setDuration(videoElement.duration);
                    setStart(0);
                    setEnd(videoElement.duration);
                    setInitialStart(0);
                    setInitialEnd(videoElement.duration);
                    setHasChanges(false);
                  }}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleVideoEnded}
                />
                <div className='absolute inset-0 flex items-center justify-center'>
                  <button
                    onClick={handlePlayPause}
                    disabled={!duration}
                    className='flex size-16 items-center justify-center rounded-full bg-black text-white opacity-0 shadow-lg transition-all duration-200 group-hover:opacity-40 hover:scale-110 hover:!opacity-100'>
                    {isPlaying ? (
                      <PauseIcon className='size-6 text-white' />
                    ) : (
                      <PlayIcon className='ml-1 size-6 text-white' />
                    )}
                  </button>
                </div>
                <div className='absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs text-white'>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='rounded-lg pt-6'>
          <div
            className='relative mb-2 h-6 w-full cursor-pointer rounded-full bg-gray-200'
            data-testid={TestIds.uploadContent.videoTrimTrack}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleTimelineClick}>
            <div className='absolute h-6 w-full rounded bg-gray-200' />
            <div
              className='absolute h-6 rounded bg-blue-500 opacity-25'
              style={{
                left: `${(start / duration) * 100}%`,
                width: `${((end - start) / duration) * 100}%`
              }}
            />
            {currentTime >= start - TOLERANCE && currentTime <= end + TOLERANCE && (
              <div
                className='absolute h-6 rounded bg-blue-400 opacity-[.64]'
                style={{
                  left: `${(start / duration) * 100}%`,
                  width: `${((currentTime - start) / (end - start)) * ((end - start) / duration) * 100}%`
                }}
              />
            )}

            <div
              className='absolute top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-blue-900 active:cursor-grabbing'
              style={{
                backgroundColor:
                  currentTime >= start - TOLERANCE && currentTime <= end + TOLERANCE
                    ? 'rgb(192, 204, 236)'
                    : 'rgba(192, 204, 236, 0.5)',
                left: `${(currentTime / duration) * 100}%`
              }}
              onMouseDown={createProgressDragHandler()}
              data-draggable='true'
            />

            <DraggableElement
              position={`${(start / duration) * 100}%`}
              isLabel={true}
              onMouseDown={createDragHandler(handleStartChange, 'start')}
              className='bg-accent-green hover:bg-green-600'
              style={{ top: '-24px' }}
              data-draggable='true'
              dataTestId={TestIds.uploadContent.videoTrimStartHandle}>
              {formatTime(start)}
            </DraggableElement>

            <DraggableElement
              position={`${(start / duration) * 100}%`}
              onMouseDown={createDragHandler(handleStartChange, 'start')}
              className='h-9 w-0.5 bg-accent-green hover:bg-green-600'
              style={{ top: '-11px' }}
              data-draggable='true'
            />

            <DraggableElement
              position={`${(end / duration) * 100}%`}
              isLabel={true}
              onMouseDown={createDragHandler(handleEndChange, 'end')}
              className='bg-accent-red hover:bg-red-600'
              style={{ top: '-24px' }}
              data-draggable='true'
              dataTestId={TestIds.uploadContent.videoTrimEndHandle}>
              {formatTime(end)}
            </DraggableElement>

            <DraggableElement
              position={`${(end / duration) * 100}%`}
              onMouseDown={createDragHandler(handleEndChange, 'end')}
              className='h-9 w-0.5 bg-accent-red hover:bg-red-600'
              style={{ top: '-11px' }}
              data-draggable='true'
            />
          </div>
          <div className='flex justify-between text-sm'>
            <span className='font-medium'>{START_TIME}</span>
            <span>
              {t('duration')}:
              <span
                className='font-medium'
                data-testid={TestIds.uploadContent.videoTrimDurationLabel}>
                {formatTime(end - start)}
              </span>
            </span>
            <span className='font-medium'>{formatTime(duration)}</span>
          </div>
        </div>

        <div className='flex flex-row-reverse items-center justify-between rounded-lg'>
          <div className='flex gap-3'>
            <Button variant={ButtonVariant.QUATERNARY} handleOnClick={closeModal} className='w-32'>
              {t('cancel')}
            </Button>
            <Button
              handleOnClick={handleSaveTrim}
              disabled={processing || !hasChanges}
              className='w-32'
              data-testid={TestIds.uploadContent.videoTrimSaveButton}>
              {processing ? (
                <div className='flex items-center'>{t('processing')}...</div>
              ) : (
                t('save')
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
